import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormHelperText,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemText,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ContentLayout,
  HeaderBreadcrumbs,
  Loader,
  LoadingButton,
  usePermission,
} from "../../../shared";
import { useManagePromosAndNotifications } from "../hooks/useManagePromosAndNotifications";
import InputControl from "../../../shared/components/InputControl";
import { DropzoneComponents } from "../../BirthdayCoupons/components";
import { FormCardLayout, PublishNotificationOrPromoALert } from "../components";
import { Delete } from "@mui/icons-material";
import { Fragment } from "react";

export const ManagePromosAndNotifications = () => {
  const {
    loading,
    state,
    title,
    validator,
    isEditMode,
    breadcrumbLinks,
    storeList,
    onSubmit,
    onChange,
    onResetImage,
    togglePublishDialog,
  } = useManagePromosAndNotifications();
  const isEditable = usePermission("promo-notification-modify");
  const isCreatable = usePermission("promo-notification-create");

  const details = state.details;

  const helperTexts = {
    name: validator.current.message("name", details.name, "required"),
    label: validator.current.message(
      "label",
      {
        label: details.label,
        description: details.description,
        image: details.image,
      },
      "atLeastOneIdentifier"
    ),
    description: validator.current.message(
      "description",
      {
        label: details.label,
        description: details.description,
        image: details.image,
      },
      "atLeastOneIdentifier"
    ),
    image: validator.current.message(
      "image",
      {
        label: details.label,
        description: details.description,
        image: details.image,
      },
      "atLeastOneIdentifier"
    ),
    startDate: validator.current.message(
      "start_date",
      details.start_date,
      "required"
    ),
    endDate: validator.current.message(
      "end_date",
      { start_date: details.start_date, end_date: details.end_date },
      "endDate"
    ),
    // store: validator.current.message("store", details.stores, "required"),
  };

  return (
    <ContentLayout
      title={
        loading.list && isEditMode ? (
          <Skeleton variant="rounded" width="160px" height="30px" />
        ) : (
          title
        )
      }
      breadcrumbs={<HeaderBreadcrumbs links={breadcrumbLinks} />}
      rightContent={
        (isEditMode && isEditable) || (isCreatable && !isEditMode) ? (
          <Stack direction="row" gap="12px">
            <LoadingButton
              variant="text"
              loading={state.loading.draft}
              disabled={state.loading.published}
              onClick={() => onSubmit({ status: "draft" })}
            >
              {isEditMode ? "Save Changes" : "Save as draft"}
            </LoadingButton>
            <LoadingButton
              variant="contained"
              onClick={() => onSubmit({ status: "published", confirm: false })}
              loading={state.loading.published}
              disabled={state.loading.draft}
            >
              Publish
            </LoadingButton>
          </Stack>
        ) : (
          <></>
        )
      }
    >
      {loading.list && isEditMode ? (
        <Stack
          sx={{
            display: "flex",
            height: "calc(100vh - 220px)",
            justifyContent: "center",
          }}
        >
          <Loader />
        </Stack>
      ) : (
        <Fragment>
          <FormCardLayout
            title="Name"
            caption="Add a name for internal reference. It will not be displayed anywhere else."
          >
            <InputControl
              type="text"
              label="Name"
              name="name"
              value={details.name}
              required
              helperText={helperTexts.name}
              error={!!helperTexts.name}
              onChange={onChange}
            />
          </FormCardLayout>
          <Divider />

          <FormCardLayout
            title="Details"
            caption="Enter a label, description, or image. These details will be shown in the POS system."
          >
            <Stack gap="20px">
              <InputControl
                type="text"
                label="Label"
                name="label"
                value={details.label}
                helperText={helperTexts.label}
                error={!!helperTexts.label}
                onChange={onChange}
              />
              <InputControl
                type="text"
                label="Description"
                name="description"
                value={details.description}
                helperText={helperTexts.description}
                error={!!helperTexts.description}
                onChange={onChange}
              />
              <Box width="400px" position="relative">
                <DropzoneComponents
                  image
                  imgUrl={details.image.url}
                  handleDropzone={(e) => {
                    onChange({ target: { name: "image", value: e } });
                  }}
                  helperText="Upload an image less than 1MB, preferably in 16:9 ratio."
                  aspect="16:9"
                  previewStyle={{ objectFit: "contain" }}
                />
                {details.image?.url && (
                  <IconButton
                    size="small"
                    onClick={onResetImage}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      backgroundColor: "rgba(0,0,0,0.6)",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.8)",
                      },
                    }}
                  >
                    <Delete fontSize="small" sx={{ color: "#fff" }} />
                  </IconButton>
                )}

                {helperTexts.image && (
                  <FormHelperText sx={{ color: "error.main" }}>
                    {helperTexts.image}
                  </FormHelperText>
                )}
              </Box>
            </Stack>
          </FormCardLayout>
          <Divider />

          <FormCardLayout
            title="Type*"
            caption="Select whether this item will appear as a promo, a notification."
          >
            <Stack flexDirection="row" gap="16px">
              <RadioGroup
                aria-labelledby="promo-and-notifications-radio-button"
                name="type"
                row
                value={details.type}
                onChange={onChange}
              >
                <FormControlLabel
                  value="promo"
                  control={<Radio />}
                  label="Promo"
                />
                <FormControlLabel
                  value="notification"
                  control={<Radio />}
                  label="Notification"
                />
              </RadioGroup>
            </Stack>
          </FormCardLayout>
          <Divider />

          <FormCardLayout title="Validity">
            <Stack flexDirection="row" gap="16px">
              <InputControl
                type="datePicker"
                name="start_date"
                label="Start Date"
                inputFormat="dd-MM-yyyy"
                minDate={new Date()}
                sx={{
                  width: "100%",
                }}
                value={details.start_date}
                onChange={(e) => {
                  onChange({
                    target: {
                      name: "start_date",
                      value: e,
                    },
                  });
                }}
                required
                helperText={helperTexts.startDate}
                error={!!helperTexts.startDate}
              />
              <InputControl
                type="datePicker"
                name="end_date"
                label="End Date"
                inputFormat="dd-MM-yyyy"
                minDate={details.start_date || new Date()}
                sx={{
                  width: "100%",
                }}
                value={details.end_date}
                onChange={(e) => {
                  onChange({
                    target: {
                      name: "end_date",
                      value: e,
                    },
                  });
                }}
                helperText={helperTexts.endDate}
                error={!!helperTexts.endDate}
              />
            </Stack>
          </FormCardLayout>
          <Divider />

          <FormCardLayout title="Stores" caption="Applies to all stores unless specific ones are selected.">
            <InputControl
              type="dropdown"
              name="store"
              label="Store"
              options={storeList}
              value={details.stores}
              multiple
              groupBy={() => "All"}
              disableCloseOnSelect
              onChange={(_, value) =>
                onChange({ target: { name: "stores", value } })
              }
              getOptionLabel={(option) => option.name}
              renderTags={() => null}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option, { selected }) => (
                <ListItem {...props} key={option.id} dense>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} />
                  {option.name}
                </ListItem>
              )}
              sx={{ width: { xs: "100%", md: "350px" } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  // required
                  label="Store"
                  placeholder="Select store"
                  // helperText={helperTexts.store}
                  // error={!!helperTexts.store}
                />
              )}
              renderGroup={(params) => {
                const isChecked = details.stores.length === storeList.length;

                const handleHeaderClick = () => {
                  onChange({
                    target: {
                      name: "stores",
                      value: isChecked ? [] : storeList,
                    },
                  });
                };
                return (
                  <Fragment key={params.group}>
                    <ListItemButton
                      onClick={handleHeaderClick}
                      sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        ":hover": {
                          cursor: "pointer",
                          backgroundColor: "rgba(0,0,0,0.04)",
                        },
                      }}
                      dense
                      selected={isChecked}
                    >
                      <Checkbox checked={isChecked} />
                      <Typography>{params.group}</Typography>
                    </ListItemButton>
                    <ListItemText>{params.children}</ListItemText>
                  </Fragment>
                );
              }}
            />
            <Stack
              display="flex"
              flexDirection="row"
              flexWrap="wrap"
              gap="8px"
              mt="16px"
            >
              {details.stores.map((store) => (
                <Chip
                  label={store.name}
                  key={store.id}
                  size="medium"
                  onDelete={() =>
                    onChange({
                      target: {
                        name: "stores",
                        value: details.stores.filter((s) => s.id !== store.id),
                      },
                    })
                  }
                />
              ))}
            </Stack>
          </FormCardLayout>
        </Fragment>
      )}

      {/* Publish Alert */}
      <PublishNotificationOrPromoALert
        open={state.dialog.publish}
        name={details?.name}
        type={details?.type}
        status={isEditMode ? details?.status : "draft"}
        onClose={() => togglePublishDialog(false)}
        onPublish={() => onSubmit({ status: "published" })}
      />
    </ContentLayout>
  );
};
